
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { da } from "date-fns/locale";

type ManualTimeEntryProps = {
  userId: string;
  tasks: any[];
  onTimeRegistered: () => void;
};

export const ManualTimeEntry = ({ userId, tasks, onTimeRegistered }: ManualTimeEntryProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [notes, setNotes] = useState("");
  const [breakMinutes, setBreakMinutes] = useState("30");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTask) {
      toast({
        title: "Vælg opgave",
        description: "Du skal vælge en opgave at registrere tid på",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const selectedTaskData = tasks.find(task => task.id === selectedTask);
      
      if (!selectedTaskData?.work_location_id) {
        toast({
          title: "Fejl ved tidsregistrering",
          description: "Opgaven har ikke en tilknyttet arbejdslokation. Kontakt support.",
          variant: "destructive",
        });
        return;
      }

      const startDateTime = new Date(`${date}T${startTime}`);
      const endDateTime = new Date(`${date}T${endTime}`);

      const { data, error } = await supabase
        .from("time_registrations")
        .insert({
          user_id: userId,
          task_id: selectedTask,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          break_minutes: parseInt(breakMinutes),
          notes,
          manual_entry: true,
          registration_type: "manual",
          status: "completed",
          work_location_id: selectedTaskData.work_location_id,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Tid registreret",
        description: "Din tid er blevet registreret på opgaven",
      });

      // Reset form
      setSelectedTask("");
      setNotes("");
      onTimeRegistered();
    } catch (error) {
      console.error("Fejl ved tidsregistrering:", error);
      toast({
        title: "Fejl ved tidsregistrering",
        description: "Der skete en fejl. Prøv igen eller kontakt support.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Select value={selectedTask} onValueChange={setSelectedTask}>
          <SelectTrigger className="w-full bg-white border border-gray-300">
            <SelectValue placeholder="Vælg opgave" className="text-gray-900" />
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-300 shadow-lg">
            {tasks.map((task) => (
              <SelectItem 
                key={task.id} 
                value={task.id}
                className="text-gray-900 hover:bg-gray-100"
              >
                {task.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full"
          />
          <Input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      <div>
        <Input
          type="number"
          value={breakMinutes}
          onChange={(e) => setBreakMinutes(e.target.value)}
          min="0"
          max="120"
          placeholder="Pause i minutter"
          className="w-full"
        />
      </div>

      <div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Noter til tidsregistrering"
          className="w-full"
        />
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Registrerer tid..." : "Registrer tid"}
      </Button>
    </form>
  );
};
